
import { Component, Vue, Emit, Prop, Watch } from "vue-property-decorator";
import { namespace, Getter } from "vuex-class";
import { validationMixin } from "vuelidate";
import { required, requiredIf, minLength, maxLength, numeric } from "vuelidate/lib/validators";
import vSelect from "vue-select";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import { UserPermissions, RoutingGroup } from "@/store/types";
import { AlarmQueueFilterEditModel, AlarmTag } from "@/store/eventqueue/types";
import AreaTreePicker from "@/components/form/AreaTreePicker.vue";
import { AreaNode } from '@/types/groups/AreaNode';

const Eventqueue = namespace("eventqueue");

@Component({
	mixins: [validationMixin],
	validations: {
		newFilter: {
			title: {
				required,
				minLength: minLength(1),
				maxLength: maxLength(250),
			},
			// if value is not empty - the operator is required
			priorityOperator: {
				required: requiredIf(function () {
					return !isNaN(parseInt(this.newFilter.priorityValue, 10));
				}),
			},
			numberOfEventsOperator: {
				required: requiredIf(function () {
					return !isNaN(parseInt(this.newFilter.numberOfEventsValue, 10));
				}),
			},
			alarmAgeOperator: {
				required: requiredIf(function () {
					return !isNaN(parseInt(this.newFilter.alarmAgeValue, 10));
				}),
			},
		},
	},
	components: {
		"v-select": vSelect,
		"area-tree-picker": AreaTreePicker,
		VuePerfectScrollbar,
	},
})
export default class AlarmQueueFilterEdit extends Vue {
	$refs!: {
		modal: any;
	};

	@Prop(Object) filter: any;
	@Prop({ type: Boolean, default: false }) multiTenant: boolean;
	@Prop({ type: Boolean, default: false }) edit: boolean;

	@Eventqueue.Getter isNewFilterModalVisible: boolean;
	@Eventqueue.Getter routingGroups: RoutingGroup[];
	@Eventqueue.Getter alarmTags: AlarmTag[];
	@Eventqueue.Mutation setNewFilterModalShown: any;
	@Eventqueue.Action createAlarmQueueFilter: any;
	@Eventqueue.Action editAlarmQueueFilter: any;
	@Eventqueue.Action loadRoutingGroups: () => Promise<any>;
	@Eventqueue.Action loadAlarmTags: () => Promise<any>;
	@Eventqueue.Action setActiveFilter: any;
	@Eventqueue.Action fetchGroupsForFilter: any;

	@Getter getPermissions: UserPermissions;

	public defaultFilter: AlarmQueueFilterEditModel = {
		title: "",
		groups: [],
		groupID: null,
		priorityValue: null,
		priorityOperator: null,
		numberOfEventsValue: null,
		numberOfEventsOperator: null,
		routingGroupID: null,
		alarmTagID: null,
		alarmAgeValue: null,
		alarmAgeOperator: null,
		shared: false,
		combinedFilters: false
	};

	public selectedArea: any = {};
	public newFilter: any = {};
	public comparisonOperators: string[] = ["Greater than", "Less than", "Equal to"];
	public hasValidationErrors: boolean = false;
	public showErrorAnimation: boolean = false;
	public showAdvancedCriteria: boolean = false;
	public isAreaPickerVisible: boolean = false;
	private areaList: any[] = [];
	private selectedTreeItem: AreaNode = null;

	@Watch("isNewFilterModalVisible")
	public async onEventDetailsShownChanged(value: boolean) {
		if (value) {
			await Promise.all([this.prepareInitialData(), this.loadRoutingGroups(), this.loadAlarmTags()]);

			this.$refs.modal.show();
		} else {
			this.$refs.modal.hide();
		}
	}

	@Watch("newFilter.title")
	public onCallsignChanged(title: string) {
		this.hasValidationErrors = !title.length;
	}

	public get submitButtonTitle() {
		return this.filter ? "Save" : "Add";
	}

	public get routingGroup() {
		return typeof this.newFilter.routingGroupID === "number"
			? this.routingGroups.filter((group) => group.routingGroupID === this.newFilter.routingGroupID)[0]
			: null;
	}

	public set routingGroup(value: RoutingGroup | null) {
		this.newFilter.routingGroupID = value ? value.routingGroupID : value;
	}

	public get alarmTag() {
		return typeof this.newFilter.alarmTagID === "number"
			? this.alarmTags.filter((tag) => tag.responseAlarmTypeID === this.newFilter.alarmTagID)[0]
			: null;
	}

	public set alarmTag(value: AlarmTag | null) {
		this.newFilter.alarmTagID = value ? value.responseAlarmTypeID : value;
	}

	public mounted() {
		this.$v.$reset();
		this.setNewFilterModalShown(false);
	}

	public async prepareInitialData() {
		this.newFilter = { ...this.defaultFilter };

		this.areaList = [];
		if (this.filter) {
			this.newFilter = {
				...this.newFilter,
				...this.filter,
			};

			if (this.newFilter.groups) {
				const filterGroups = await this.fetchGroupsForFilter(this.newFilter.alarmQueueFilterID);
				if (filterGroups) {
					this.newFilter.groups.forEach((filterGroup: any) => {
						let groups = filterGroups.filter((listGroup) => listGroup.groupID == filterGroup.groupID);
						if (groups && groups.length > 0) {
							this.areaList.push({
								groupID: filterGroup.groupID,
								title: groups[0].title,
								inherited: filterGroup.inherited,
							});
						}
					});
				}
			}

			this.showAdvancedCriteria = true;
		}

		this.isAreaPickerVisible = false;
	}

	@Emit("submit")
	public async onSubmit() {
		this.$v.$touch();

		if (this.$v.newFilter.$error) {
			this.showErrorAnimation = true;

			setTimeout(() => {
				this.showErrorAnimation = false;
			}, this.$config.ANIMATION_DURATION);
		} else {
			this.newFilter.groups = this.areaList;
			this.newFilter.created = new Date().toISOString();

			const saveFn = this.filter ? "editAlarmQueueFilter" : "createAlarmQueueFilter";
			const alarmQueueFilterID = await this[saveFn](this.newFilter);
			this.setNewFilterModalShown(false);

			this.setActiveFilter({
				filterId: alarmQueueFilterID,
				regionId: null,
			});

			return alarmQueueFilterID;
		}
	}

	@Emit("cancel")
	public onCancel() {
		this.setNewFilterModalShown(false);
		return;
	}

	/**
	 * Handler for when the user selects an item in the area tree. Possible nodes include groups and cameras.
	 */
	private onTreeItemSelected(treeItem: AreaNode) {
		this.selectedTreeItem = treeItem;

		this.$set(this, "selectedArea", {
			title: treeItem.label,
			groupID: treeItem.id,
		});

		if (!this.areaList.some((area: any) => area.groupID === treeItem.id)) {
			this.areaList.push({
				groupID: treeItem.id,
				title: treeItem.label,
				inherited: true,
			});
		}

		this.isAreaPickerVisible = false;
	}

	public removeArea(removeArea: any) {
		this.areaList = this.areaList.filter((area: any) => area.groupID != removeArea.groupID);
	}
}
